import { useEffect, useState, useCallback } from "react";
import { X, Sparkles, Lock, Check, Loader2, LogIn, LogOut, ShoppingBag, Trophy, Zap } from "lucide-react";
import { useAuth } from "@/lib/auth";
import SkinPreview3D from "@/components/SkinPreview3D";

const API = process.env.REACT_APP_BACKEND_URL;

// Race-themed visual tokens. Each cosmic race has its own signature palette.
const RACE = {
  human:   { label: "HUMANO",   ring: "border-slate-400/60",   glow: "shadow-slate-400/40",   text: "text-slate-200",   bg: "from-slate-900/60 to-slate-950/80" },
  aeryn:   { label: "AERYN",    ring: "border-emerald-400/80",  glow: "shadow-emerald-400/60", text: "text-emerald-300", bg: "from-emerald-950/60 to-teal-950/80" },
  thryx:   { label: "THRYX",    ring: "border-purple-400/70",   glow: "shadow-purple-400/60",  text: "text-purple-300",  bg: "from-purple-950/60 to-fuchsia-950/80" },
  xelnar:  { label: "XEL'NAR",  ring: "border-cyan-300/80",     glow: "shadow-cyan-300/60",    text: "text-cyan-200",    bg: "from-cyan-950/60 to-sky-950/80" },
  oryxae:  { label: "ORYXAE",   ring: "border-orange-400/80",   glow: "shadow-orange-400/60",  text: "text-orange-300",  bg: "from-orange-950/60 to-red-950/80" },
  korvesh: { label: "KOR'VESH", ring: "border-red-500/70",      glow: "shadow-red-500/60",     text: "text-red-300",     bg: "from-red-950/60 to-rose-950/80" },
  lythari: { label: "LYTHARI",  ring: "border-amber-300/90",    glow: "shadow-amber-300/70",   text: "text-amber-200",   bg: "from-amber-900/60 to-yellow-950/80" },
  kryon:   { label: "KRYON",    ring: "border-indigo-400/80",   glow: "shadow-indigo-400/60",  text: "text-indigo-200",  bg: "from-indigo-950/60 to-violet-950/80" },
  nerevan: { label: "NEREVAN",  ring: "border-pink-400/80",     glow: "shadow-pink-400/60",    text: "text-pink-200",    bg: "from-pink-950/60 to-rose-950/80" },
  umbrax:  { label: "UMBRAX",   ring: "border-zinc-400/70",     glow: "shadow-zinc-400/50",    text: "text-zinc-200",    bg: "from-zinc-900/60 to-neutral-950/80" },
};
const RACE_FALLBACK = RACE.human;

function priceLabel(cents) {
  if (!cents) return "GRÁTIS";
  return `$${(cents / 100).toFixed(2)}`;
}

function applyDiscount(cents, percentOff) {
  if (!cents || !percentOff) return cents;
  return Math.round(cents * (100 - percentOff) / 100);
}

const TIER_COLORS = {
  cadet:     { ring: "border-slate-500/50",  text: "text-slate-300",  glow: "shadow-slate-500/40" },
  recruit:   { ring: "border-cyan-400/70",   text: "text-cyan-300",   glow: "shadow-cyan-400/50" },
  pilot:     { ring: "border-primary/70",    text: "text-primary",    glow: "shadow-primary/50" },
  captain:   { ring: "border-purple-400/70", text: "text-purple-300", glow: "shadow-purple-400/60" },
  commander: { ring: "border-amber-300/80",  text: "text-amber-200",  glow: "shadow-amber-300/60" },
};

// Card preview. When the skin has a real (non-placeholder) .glb, we render an actual
// rotating 3D preview (lazy-loaded via IntersectionObserver in SkinPreview3D).
// Placeholder / coming-soon skins keep the SVG silhouette + "MODELO 3D EM BREVE" overlay.
function SkinPreview({ skin }) {
  const r = RACE[skin.race] || RACE_FALLBACK;
  const primary = skin.color || "#94a3b8";
  const glow = skin.glow_color || "#e2e8f0";
  const isComingSoon = skin.coming_soon_model;
  const has3D = skin.model_type === "glb" && skin.model_url && !isComingSoon;
  return (
    <div className={`relative aspect-video rounded-sm border ${r.ring} bg-gradient-to-br ${r.bg} overflow-hidden shadow-lg ${r.glow}`}>
      {/* grid floor (behind the ship) */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: `linear-gradient(${glow}22 1px, transparent 1px), linear-gradient(90deg, ${glow}22 1px, transparent 1px)`,
        backgroundSize: "24px 24px",
        transform: "perspective(300px) rotateX(60deg) translateY(30px)",
        transformOrigin: "bottom",
      }} />

      {has3D ? (
        <SkinPreview3D modelUrl={skin.model_url} tint={skin.color} glow={skin.glow_color} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg viewBox="0 0 120 60" width="60%" style={{ filter: `drop-shadow(0 0 14px ${glow})` }}>
            <defs>
              <linearGradient id={`g-${skin.skin_id}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={primary} />
                <stop offset="100%" stopColor={glow} />
              </linearGradient>
            </defs>
            {skin.race === "human" ? (
              <>
                <ellipse cx="60" cy="35" rx="42" ry="8" fill={`url(#g-${skin.skin_id})`} />
                <ellipse cx="60" cy="30" rx="20" ry="10" fill={glow} opacity="0.7" />
              </>
            ) : (
              <>
                <polygon points="60,10 90,40 60,52 30,40" fill={`url(#g-${skin.skin_id})`} />
                <polygon points="60,18 78,38 60,44 42,38" fill={glow} opacity="0.9" />
                <circle cx="60" cy="38" r="3" fill="#fff" />
              </>
            )}
          </svg>
        </div>
      )}

      {/* race tag */}
      <div className={`absolute top-2 left-2 font-mono text-[9px] tracking-widest px-2 py-1 border ${r.ring} ${r.text} bg-black/50 rounded-sm z-10`}>
        {skin.race_label || r.label}
      </div>
      {/* ship class tag */}
      {skin.ship_class && (
        <div className="absolute top-2 right-2 font-mono text-[9px] tracking-widest px-2 py-1 border border-white/20 text-white/70 bg-black/50 rounded-sm z-10">
          {skin.ship_class.toUpperCase()}
        </div>
      )}
      {/* coming-soon overlay */}
      {isComingSoon && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-10"
             data-testid={`skin-coming-soon-${skin.skin_id}`}>
          <div className="font-display font-black text-lg tracking-tighter text-white/90 border border-white/30 px-3 py-1 rounded-sm">
            MODELO 3D EM BREVE
          </div>
        </div>
      )}
    </div>
  );
}

export default function ShopModal({ open, onClose, onSkinChange }) {
  const { user, loading, login, logout } = useAuth();
  const [skins, setSkins] = useState([]);
  const [ownership, setOwnership] = useState({ owned: [], equipped: "ufo_classic" });
  const [stats, setStats] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);

  const loadOwnership = useCallback(async () => {
    if (!user) { setOwnership({ owned: [], equipped: "ufo_classic" }); setStats(null); return; }
    try {
      const [ownR, statsR] = await Promise.all([
        fetch(`${API}/api/shop/me/skins`, { credentials: "include" }),
        fetch(`${API}/api/pilot/me/stats`, { credentials: "include" }),
      ]);
      if (ownR.ok) setOwnership(await ownR.json());
      if (statsR.ok) setStats(await statsR.json());
    } catch (err) {
      console.warn("[shop] failed to load user data:", err);
    }
  }, [user]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/shop/skins`).then((r) => r.json()).then((d) => setSkins(d.skins || [])).catch(() => {});
    loadOwnership();
  }, [open, loadOwnership]);

  const buy = async (skin) => {
    if (!user) { login(); return; }
    setBusyId(skin.skin_id); setErr(null);
    try {
      const r = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lookup_key: skin.lookup_key, origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao iniciar pagamento");
      window.location.href = d.checkout_url;
    } catch (e) {
      setErr(e.message);
      setBusyId(null);
    }
  };

  const equip = async (skin) => {
    if (!user) return;
    setBusyId(skin.skin_id); setErr(null);
    try {
      const r = await fetch(`${API}/api/shop/equip/${skin.skin_id}`, {
        method: "POST", credentials: "include",
      });
      if (!r.ok) throw new Error("Falha ao equipar");
      const d = await r.json();
      setOwnership((o) => ({ ...o, equipped: d.equipped }));
      onSkinChange?.(skin);
    } catch (e) {
      setErr(e.message);
    } finally { setBusyId(null); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6" data-testid="shop-modal">
      <div className="relative w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-sm border border-primary/40 bg-gradient-to-b from-[#050a18] to-[#020408]">
        {/* Milky Way ambient backdrop — consistent with in-game aesthetic */}
        <img
          src="/stars_milky_way.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-90 pointer-events-none select-none z-0"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050a18]/40 via-[#020408]/30 to-[#020408]/50 pointer-events-none z-0" />
        <div className="relative z-10 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary/30">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-5 w-5 text-primary" />
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">HANGAR // MERCADO NEXUS</p>
              <h2 className="font-display font-black text-2xl tracking-tighter text-white">LOJA DE NAVES</h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-2 text-xs font-mono">
                {user.picture && <img src={user.picture} alt="" className="h-7 w-7 rounded-full border border-primary/40" />}
                <span className="hidden sm:inline text-muted-foreground">{user.name}</span>
                <button onClick={logout} data-testid="shop-logout" className="p-2 border border-primary/30 rounded-sm hover:bg-primary/10 text-primary">
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button onClick={login} data-testid="shop-login" className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-[10px]">
                <LogIn className="h-3.5 w-3.5" /> ENTRAR COM GOOGLE
              </button>
            )}
            <button onClick={onClose} data-testid="shop-close" className="p-2 border border-primary/30 rounded-sm hover:bg-primary/10 text-primary">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(92vh-80px)]">
          {!loading && !user && (
            <div className="mb-6 rounded-sm border border-secondary/40 bg-secondary/5 p-4 text-sm text-secondary/90 font-mono flex items-center gap-3" data-testid="shop-auth-hint">
              <Sparkles className="h-4 w-4" />
              Entre com sua conta Google para comprar e equipar skins. As compras ficam ligadas à sua identidade galáctica.
            </div>
          )}

          {/* Pilot rank / discount panel */}
          {user && stats && (() => {
            const t = stats.tier || { tier: "cadet", label: "CADETE", percent_off: 0 };
            const nt = stats.next_tier;
            const c = TIER_COLORS[t.tier] || TIER_COLORS.cadet;
            const progressPct = nt ? Math.min(100, Math.round((stats.best_score / nt.min_score) * 100)) : 100;
            return (
              <div className={`mb-6 rounded-sm border ${c.ring} bg-black/40 p-4 shadow-lg ${c.glow}`} data-testid="pilot-rank-panel">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <Trophy className={`h-5 w-5 ${c.text}`} />
                    <div>
                      <p className="font-mono text-[9px] tracking-[0.3em] text-muted-foreground">RANK DE PILOTO</p>
                      <p className={`font-display font-black text-xl tracking-tighter ${c.text}`} data-testid="pilot-rank-label">{t.label}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[9px] tracking-[0.3em] text-muted-foreground">MELHOR SCORE</p>
                    <p className={`font-display font-black text-xl tracking-tighter ${c.text}`} data-testid="pilot-best-score">{stats.best_score.toLocaleString()}</p>
                  </div>
                  {t.percent_off > 0 && (
                    <div className={`inline-flex items-center gap-2 px-3 py-2 border ${c.ring} rounded-sm bg-black/60`} data-testid="pilot-discount-badge">
                      <Zap className={`h-4 w-4 ${c.text}`} />
                      <span className={`font-mono text-xs tracking-widest ${c.text}`}>−{t.percent_off}% NA LOJA</span>
                    </div>
                  )}
                </div>
                {nt && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between font-mono text-[10px] tracking-widest text-muted-foreground mb-1">
                      <span>PRÓXIMO RANK: {nt.label}</span>
                      <span>{stats.best_score.toLocaleString()} / {nt.min_score.toLocaleString()} · −{nt.percent_off}%</span>
                    </div>
                    <div className="h-2 rounded-sm bg-black/60 border border-white/10 overflow-hidden">
                      <div className={`h-full ${c.text.replace("text-", "bg-")}`} style={{ width: `${progressPct}%` }} />
                    </div>
                  </div>
                )}
                {!nt && (
                  <p className="mt-3 font-mono text-[10px] tracking-widest text-amber-200">RANK MÁXIMO ATINGIDO · 50% DE DESCONTO PERMANENTE</p>
                )}
              </div>
            );
          })()}

          {err && (
            <div className="mb-4 rounded-sm border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-300 font-mono">
              {err}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {skins.map((s) => {
              const r = RACE[s.race] || RACE_FALLBACK;
              const owned = ownership.owned.includes(s.skin_id);
              const equipped = ownership.equipped === s.skin_id;
              const isFree = !s.price_cents;
              const pctOff = stats?.tier?.percent_off || 0;
              const finalCents = pctOff && s.price_cents ? applyDiscount(s.price_cents, pctOff) : s.price_cents;
              return (
                <div key={s.skin_id} data-testid={`skin-card-${s.skin_id}`} className={`relative rounded-sm border ${r.ring} bg-black/60 p-4 flex flex-col gap-3 transition-all hover:-translate-y-1 hover:${r.glow} hover:shadow-2xl`}>
                  <SkinPreview skin={s} />
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className={`font-display font-black text-lg tracking-tight ${r.text}`}>{s.name}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.description}</p>
                  </div>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex flex-col leading-tight">
                      {pctOff && s.price_cents ? (
                        <>
                          <span className="font-mono text-[10px] line-through text-muted-foreground" data-testid={`skin-price-original-${s.skin_id}`}>{priceLabel(s.price_cents)}</span>
                          <span className={`font-mono text-sm ${r.text}`} data-testid={`skin-price-final-${s.skin_id}`}>{priceLabel(finalCents)}</span>
                        </>
                      ) : (
                        <span className={`font-mono text-sm ${r.text}`} data-testid={`skin-price-final-${s.skin_id}`}>{priceLabel(s.price_cents)}</span>
                      )}
                    </div>
                    {equipped ? (
                      <span data-testid={`skin-equipped-${s.skin_id}`} className="inline-flex items-center gap-1 font-mono text-[10px] tracking-widest text-primary border border-primary/40 rounded-sm px-2 py-1">
                        <Check className="h-3 w-3" /> EQUIPADA
                      </span>
                    ) : owned || isFree ? (
                      <button
                        onClick={() => equip(s)}
                        disabled={busyId === s.skin_id || (!owned && !isFree)}
                        data-testid={`skin-equip-${s.skin_id}`}
                        className="inline-flex items-center gap-1 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-[10px] px-3 py-2 disabled:opacity-50"
                      >
                        {busyId === s.skin_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "EQUIPAR"}
                      </button>
                    ) : (
                      <button
                        onClick={() => buy(s)}
                        disabled={busyId === s.skin_id}
                        data-testid={`skin-buy-${s.skin_id}`}
                        className={`inline-flex items-center gap-1 rounded-sm border ${r.ring} ${r.text} hover:bg-white/5 font-mono tracking-widest text-[10px] px-3 py-2 disabled:opacity-50`}
                      >
                        {busyId === s.skin_id ? <Loader2 className="h-3 w-3 animate-spin" /> : (<><Lock className="h-3 w-3" /> COMPRAR</>)}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-6 font-mono text-[10px] tracking-widest text-muted-foreground text-center">
            PAGAMENTOS SEGUROS VIA STRIPE · JOGUE MAIS PRA DESBLOQUEAR MAIS DESCONTOS · CARTÃO TESTE: 4242 4242 4242 4242
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
